// Resolves which site's Radio thread applies to the signed-in user.
//
// Field personnel (guards, patrol officers, site supervisors) never pick a site
// manually: the site comes from their open time_entries punch, falling back to
// their existing site assignments when exactly one applies. Dispatch, admins
// and owners may choose from the sites they are already authorized to access.
// The database still enforces authorization (has_site_access) when the Radio
// thread is resolved.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import {
  canSelectRadioSite,
  getSelectedRadioSiteId,
  resolveSelectableRadioSite,
  setSelectedRadioSiteId,
  subscribeToRadioSiteSelection,
} from "@/lib/radio/radio-site-selection";

export type RadioSite = { id: string; name: string };

export type RadioContext = {
  /** Automatically resolved site for field personnel (may be null). */
  activeSite: RadioSite | null;
  /** Sites the user is authorized for; only used by selector roles. */
  authorizedSites: RadioSite[];
  /** True when the role is allowed to choose a site instead of auto-resolving. */
  canSelectSite: boolean;
  loading: boolean;
};

export function useRadioContext(): RadioContext {
  const { currentOrg, user, currentRole, isPlatformAdmin } = useAuth();
  const orgId = currentOrg?.id ?? "";
  const userId = user?.id ?? "";
  const canSelectSite = canSelectRadioSite(currentRole, isPlatformAdmin);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(() =>
    getSelectedRadioSiteId(orgId),
  );

  useEffect(() => {
    setSelectedSiteId(getSelectedRadioSiteId(orgId));
    return subscribeToRadioSiteSelection(orgId, setSelectedSiteId);
  }, [orgId]);

  const { data, isLoading } = useQuery({
    queryKey: ["radio", "context", orgId, userId, canSelectSite, selectedSiteId],
    enabled: !!orgId && !!userId,
    staleTime: 60_000,
    queryFn: async (): Promise<{ activeSite: RadioSite | null; authorizedSites: RadioSite[] }> => {
      // 1. Open punch wins — that is where the user is actually working.
      const { data: punch } = await supabase
        .from("time_entries")
        .select("site_id")
        .eq("organization_id", orgId)
        .eq("officer_id", userId)
        .is("clock_out_at", null)
        .order("clock_in_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      // 2. Existing site assignments (RLS already scopes these).
      const { data: assignments } = await supabase
        .from("user_site_assignments")
        .select("site_id")
        .eq("organization_id", orgId)
        .eq("user_id", userId);
      const assignedIds = Array.from(
        new Set((assignments ?? []).map((a: any) => a.site_id).filter(Boolean) as string[]),
      );

      // 3. Selector roles list the sites they can read; RLS filters them.
      let authorizedSites: RadioSite[] = [];
      if (canSelectSite) {
        const { data: sites } = await supabase
          .from("sites")
          .select("id, name")
          .eq("organization_id", orgId)
          .eq("active", true)
          .order("name");
        authorizedSites = (sites ?? []) as RadioSite[];
      } else if (assignedIds.length) {
        const { data: sites } = await supabase
          .from("sites")
          .select("id, name")
          .in("id", assignedIds)
          .order("name");
        authorizedSites = (sites ?? []) as RadioSite[];
      }

      const punchedSiteId = punch?.site_id as string | null;
      const activeSiteId = canSelectSite
        ? null
        : punchedSiteId ?? (assignedIds.length === 1 ? assignedIds[0] : null);

      let activeSite: RadioSite | null = canSelectSite
        ? resolveSelectableRadioSite(selectedSiteId, authorizedSites, punchedSiteId)
        : authorizedSites.find((s) => s.id === activeSiteId) ?? null;
      if (!activeSite && activeSiteId) {
        const { data: site } = await supabase
          .from("sites")
          .select("id, name")
          .eq("id", activeSiteId)
          .maybeSingle();
        activeSite = (site as RadioSite | null) ?? null;
      }

      return { activeSite, authorizedSites };
    },
  });

  useEffect(() => {
    if (!canSelectSite || !selectedSiteId || isLoading) return;
    if (!data?.authorizedSites.some((site) => site.id === selectedSiteId)) {
      setSelectedRadioSiteId(orgId, null);
    }
  }, [canSelectSite, data?.authorizedSites, isLoading, orgId, selectedSiteId]);

  return {
    activeSite: data?.activeSite ?? null,
    authorizedSites: data?.authorizedSites ?? [],
    canSelectSite,
    loading: isLoading,
  };
}
